import useEventListener from '@util/hooks/use-event-listener/use-event-listener.hook';
import { RefObject, useCallback } from 'react';

/**
 * Custom hook to detect clicks outside of a specified element or elements.
 *
 * @param ref - A single ref or an array of refs to the elements to detect outside clicks for.
 * @param onClickOutside - Optional callback function to be called when a click outside the specified element(s) is detected.
 *
 * @returns void
 */
export default function useClickOutside<
  ElementType extends Element | null,
  RefType extends RefObject<ElementType | null> | ElementType = RefObject<ElementType | null>,
>(ref: RefType | RefType[], onClickOutside?: () => void) {
  // #region Handlers
  const handleOnMouseDown = useCallback(
    (e: Event) => {
      const isNotContain = (Array.isArray(ref) ? ref : [ref]).every((item) => {
        if (item && 'current' in item) {
          return !(item as RefObject<ElementType | null>).current?.contains(e.target as Node);
        }

        return !(item as ElementType)?.contains(e.target as Node);
      });

      if (e.target && isNotContain) {
        onClickOutside?.();
      }
    },
    [onClickOutside, ref],
  );
  // #endregion

  // #region Effects
  useEventListener({
    eventType: 'mousedown',
    listener: handleOnMouseDown,
    listenTo: 'document',
  });
  // #endregion
}
